export const iosview = {
    // "window.App.isIosPwa" returns true if the app version is the new iOS PWA build.
    // This check is necessary to ensure compatibility with both the new iOS PWA build and the old native app versions.

    // Helper function to safely call webkit messageHandlers
    safeWebkitPostMessage: (handlerName, message) => {
        try {
            if (
                window.webkit &&
                window.webkit.messageHandlers &&
                window.webkit.messageHandlers[handlerName] &&
                typeof window.webkit.messageHandlers[handlerName].postMessage === 'function'
            ) {
                window.webkit.messageHandlers[handlerName].postMessage(message);
                return true;
            }
        } catch (error) {
            if (window.Sentry) {
                window.Sentry.addBreadcrumb({
                    message: 'Webkit messageHandler call failed',
                    level: 'warning',
                    data: {
                        handler_name: handlerName,
                        error_message: error.message,
                        user_agent: navigator.userAgent,
                    },
                });
            }
        }
        return false;
    },

    salesButtonSeen: () => {
        if (!window.App.isIosPwa) {
            iosview.safeWebkitPostMessage('salesPage', 'salesButtonSeen');
        }
    },
    salesButtonClicked: () => {
        if (window.App.isIosPwa) {
            let appleProductId = window.App.product.app_product_id.product;
            window.ipse.dispatch({
                action: 'button_clicked',
                data_event: {
                    button: 'ios_sales_button_clicked',
                },
            });
            iosview.safeWebkitPostMessage('iap-purchase-request', appleProductId);
            // returns iap-purchase-result || iap-purchase-result-failure
        } else {
            iosview.safeWebkitPostMessage('salesPage', 'salesButtonClicked');
        }
    },
    pwaGetIapUpsellProducts: (products) => {
        iosview.safeWebkitPostMessage('iap-upsell-products-request', products);
        // returns iap-some-products-result || iap-some-products-result-failure
    },
    async skipButtonClicked() {
        if (window.App.isIosPwa) {
            try {
                const response = await fetch(
                    '/api/sales-page/no-thanks?' +
                    new URLSearchParams({
                        psh: window.App.sessionId,
                        uid: window.App.contact.ips_id,
                        lang: window.App.language,
                    }),
                    {
                        headers: {
                            'X-Requested-With': 'mobile',
                            Accept: 'application/json',
                        },
                    },
                );

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                if (data.upsell_data && data.upsell_data.upsell_app_product_id) {
                    this.parseIosPwaProductData(data.upsell_data);
                } else {
                    if (data.redirect_url) {
                        window.location.href = data.redirect_url + '&iosview&iospwa';
                    } else {
                        window.location.href = data.upsell_data.upsell_url;
                    }
                }
                window.App.no_thanks_clicked = true;
            } catch (error) {
                // Handle AbortError specifically to prevent unhandled promise rejections
                if (error.name === 'AbortError') {
                    console.log('iOS skip button request was aborted (this is usually not an error)');
                    if (window.Sentry) {
                        window.Sentry.addBreadcrumb({
                            message: 'iOS skip button request aborted',
                            level: 'info',
                            data: {
                                error_name: error.name,
                                user_agent: navigator.userAgent,
                                session_id: window.App.sessionId,
                            }
                        });
                    }
                } else {
                    console.error('iOS skip button error:', error);
                    if (window.Sentry) {
                        window.Sentry.captureException(error, {
                            tags: {
                                component: 'ios_skip_button'
                            },
                            extra: {
                                session_id: window.App.sessionId,
                                contact_id: window.App.contact?.ips_id,
                            }
                        });
                    }
                }
            }
        } else {
            iosview.safeWebkitPostMessage('salesPage', 'skipButtonClicked');
        }
    },
    lifetimeUpdatesButtonClicked: () => {
        if (window.App.isIosPwa) {
            window.ipse.dispatch({
                action: 'purchase_lifetime_updates_clicked',
            });
            let appleProductId = window.App.product.app_product_id.lu;
            iosview.safeWebkitPostMessage('iap-purchase-request', appleProductId);
        } else {
            iosview.safeWebkitPostMessage('salesPage', 'lifetimeUpdatesButtonClicked');
        }
    },
    skipLifetimeUpdatesButtonClicked: () => {
        if (window.App.isIosPwa) {
            window.ipse.dispatch({
                action: 'no_thanks_lifetime_updates_clicked',
            });
            window.location.href = window.App.ios_upsell_url;
        } else {
            iosview.safeWebkitPostMessage('salesPage', 'skipLifetimeUpdatesButtonClicked');
        }
    },
    timerEndtime: (endtime) => {
        if (!window.App.isIosPwa) {
            iosview.safeWebkitPostMessage('timer', endtime);
        }
    },
    discountPercentage: (percentage) => {
        if (!window.App.isIosPwa) {
            iosview.safeWebkitPostMessage('discountPercentage', percentage);
        }
    },
    paymentSessionId: (psh) => {
        if (!window.App.isIosPwa) {
            iosview.safeWebkitPostMessage('psh', psh);
        }
    },
    parseIosPwaProductData(eventData) {
        const upsellProducts = [
            eventData.upsell_app_product_id.product,
            ...(eventData.upsell_app_product_id.lu && eventData.upsell_app_product_id.lu !== 'null'
                ? [eventData.upsell_app_product_id.lu]
                : []),
        ];
        window.App.ios_temp_upsell_url = eventData.upsell_url;
        if (eventData.upsell_price_coefficient) {
            window.App.ios_upsell_price_coefficient = eventData.upsell_price_coefficient;
        }
        this.pwaGetIapUpsellProducts(upsellProducts);
    },
    topicsLinkClicked(data) {
        window.App.no_thanks_clicked = true;
        this.parseIosPwaProductData(data.upsell_data);
    },
    // Backward compatibility: Send finalized message for StoreKit 1 legacy support
    pwaInAppPurchaseFinalized() {
        iosview.safeWebkitPostMessage('iap-purchase-request-finalized', '');
    },
};

/**
 * A helper function to parse the query param price from the iOS view
 * @param {string} fullPrice The full price string from the iOS view (Url Decoded); the price is expected to be in the format: <currencySymbol><price><currency>
 * @returns {Object | void} {price, currencySymbol, currency}
 */
export function parseIosViewPrice(fullPrice) {
    const matches = fullPrice.match(/(?<curr_symb>[^\d.]*)(?<price>\d.*\d)(?<curr>[^\d.]*)/);
    if (!matches?.groups || !matches?.groups['price'] || (!matches?.groups['curr_symb'] && !matches?.groups['curr'])) {
        return;
    }
    // match the decimal part of the price as per ios examples. e.g. 1,000.00 => .00
    const priceDecimalPart = matches.groups['price'].match(/[^\d]+\d{1,2}$/);
    let price = matches.groups['price'];
    if (priceDecimalPart?.length) {
        // replace all non-digit characters except the decimal character, then replace the decimal character with a dot
        price = +price
            .replace(new RegExp(`[^\\d${priceDecimalPart[0][0]}]`, 'g'), '')
            .replace(priceDecimalPart[0][0], '.');
    } else {
        price = +price.replace(/[^\d]/g, '');
    }
    const currencySymbol = matches.groups['curr_symb'] || '';
    const currency = matches.groups['curr'] || '';
    return {
        price,
        currencySymbol,
        currency,
    };
}

/**
 *
 * @returns {Function} A function to prevent swipe gestures on the browser history
 */
export function preventBrowserHistorySwipeGestures() {
    function touchStart(ev) {
        if (ev.touches.length === 1) {
            const touch = ev.touches[0];
            if (touch.clientX < window.innerWidth * 0.1 || touch.clientX > window.innerWidth * 0.9) {
                ev.preventDefault();
            }
        }
    }

    // Safari defaults to passive: true for the touchstart event, so we need  to explicitly specify false
    // See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
    const options = { passive: false };

    window.addEventListener('touchstart', touchStart, options);

    return () => window.removeEventListener('touchstart', touchStart, options);
}
